import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronDown, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react";

export interface EnhancedSelectOption {
  value: string;
  label: string;
  emoji?: string;
  suggested?: boolean;
  color?: string;
}

export interface EnhancedSingleSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  options?: EnhancedSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  /** Required state reported by the surrounding form (`FormLabel required`). */
  "aria-required"?: boolean | "true" | "false";
  maxHeight?: number;
  renderOption?: (option: EnhancedSelectOption) => React.ReactNode;
  error?: boolean;
  /** When provided, delegates search to the parent instead of filtering client-side */
  onSearchChange?: (term: string) => void;
  /** Shows a loading indicator while server-side search is in progress */
  isSearching?: boolean;
  /**
   * A failed server-side search. Takes precedence over the "no results" empty
   * state so a failed query is never reported as "nothing matched".
   */
  isError?: boolean;
  /** Localized message rendered in place of "no results" when `isError`. */
  errorMessage?: string;
  /** Extra content rendered in the search header area (e.g., filter toggles) */
  headerContent?: React.ReactNode;
}

const EnhancedSingleSelect = React.forwardRef((props: EnhancedSingleSelectProps, ref: React.Ref<HTMLButtonElement>) => {
  const {
    value = "",
    onChange,
    options = [],
    placeholder,
    disabled,
    className,
    id,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    maxHeight = 300,
    renderOption,
    error,
    onSearchChange,
    isSearching,
    isError,
    errorMessage,
    headerContent,
  } = props;

  const { i18n } = useLingui();
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");

  const closePopover = React.useCallback(() => {
    setOpen(false);
    setSearchTerm("");
    onSearchChange?.("");
  }, [onSearchChange]);

  // Generate unique IDs for accessibility
  const popoverContentId = React.useId();

  // When rendered inside a Dialog/Sheet, react-remove-scroll (used by Radix Dialog)
  // blocks wheel-driven scrolling on portaled elements outside the Dialog's DOM tree.
  // We work around this by imperatively scrolling the container and suppressing the
  // native wheel behaviour so react-remove-scroll's document handler has no effect.
  //
  // We use a callback ref (not useRef + useEffect) because Radix Portal uses a
  // two-phase mount: first render returns null, then a useLayoutEffect sets mounted=true
  // and re-renders with createPortal. The parent component doesn't re-render for phase 2,
  // so a useEffect here would miss the DOM node. A callback ref fires the instant the
  // node is inserted into the DOM, regardless of which render cycle creates it.
  const scrollRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    node.addEventListener("wheel", (e: WheelEvent) => {
      const delta = e.deltaMode === 1 ? e.deltaY * 20 : e.deltaY;
      node.scrollTop += delta;
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false });
  }, []);

  // Separate suggested and regular options
  const suggestedOptions = React.useMemo(
    () => options.filter((opt) => opt.suggested),
    [options]
  );

  const regularOptions = React.useMemo(
    () => options.filter((opt) => !opt.suggested),
    [options]
  );

  // Filter options based on search term
  // When server-side search is active (onSearchChange provided), skip client-side filtering
  const filteredSuggested = React.useMemo(
    () =>
      onSearchChange
        ? suggestedOptions
        : suggestedOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              opt.value.toLowerCase().includes(searchTerm.toLowerCase())
          ),
    [suggestedOptions, searchTerm, onSearchChange]
  );

  const filteredRegular = React.useMemo(
    () =>
      onSearchChange
        ? regularOptions
        : regularOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              opt.value.toLowerCase().includes(searchTerm.toLowerCase())
          ),
    [regularOptions, searchTerm, onSearchChange]
  );

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    closePopover();
  };

  const selectedOption = React.useMemo(() => {
    return options.find((opt) => opt.value === value);
  }, [value, options]);

  const selectedLabel = React.useMemo(() => {
    if (!selectedOption) return "";
    return `${selectedOption.emoji || ""} ${selectedOption.label}`.trim();
  }, [selectedOption]);

  const renderOptionItem = (option: EnhancedSelectOption) => {
    const isSelected = value === option.value;

    return (
      <div
        key={option.value}
        className={cn(
          "flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer w-full",
          isSelected && "bg-muted"
        )}
        onClick={() => handleSelect(option.value)}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          {renderOption ? (
            renderOption(option)
          ) : option.color ? (
            <Badge
              variant="secondary"
              style={{
                backgroundColor: option.color ? `${option.color}20` : undefined,
                borderColor: option.color || undefined,
                color: option.color || undefined,
              }}
              className="cursor-pointer truncate"
            >
              {option.label}
            </Badge>
          ) : (
            <>
              {option.emoji && (
                <span key="emoji" className="text-lg flex-shrink-0">{option.emoji}</span>
              )}
              <Label key="label" className="cursor-pointer flex-1 truncate">
                {option.label}
              </Label>
            </>
          )}
        </div>
        {isSelected && (
          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
        )}
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={(isOpen) => {
      if (isOpen) {
        setOpen(true);
        return;
      }
      // When headerContent is present, ignore Radix's automatic close events.
      // Closing is handled manually: handleSelect, onInteractOutside, onEscapeKeyDown.
      if (headerContent) return;
      closePopover();
    }}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          id={id}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-required={ariaRequired}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={popoverContentId}
          aria-haspopup="listbox"
          className={cn(
            "w-full justify-between text-left font-normal",
            !value && "text-muted-foreground",
            error && "border-destructive",
            className
          )}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption ? (
              renderOption ? (
                renderOption(selectedOption)
              ) : (
                selectedLabel
              )
            ) : (
              placeholder
            )}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        id={popoverContentId}
        role="listbox"
        className="w-[--radix-popover-trigger-width] p-0 min-w-[200px] max-w-[400px]"
        align="start"
        sideOffset={5}
        avoidCollisions={true}
        collisionPadding={10}
        {...(headerContent ? {
          onInteractOutside: () => closePopover(),
          onEscapeKeyDown: () => closePopover(),
        } : {})}
      >
        <div className="sticky top-0 z-10 bg-background p-3 border-b">
          {headerContent && (
            <div className="mb-2">
              {headerContent}
            </div>
          )}
          <div className="relative">
            {isSearching ? (
              <Loader2 className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground animate-spin" aria-hidden="true" />
            ) : (
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
            )}
            <Input
              placeholder={i18n._({
                id: "common.multiselect.searchPlaceholder",
                message: "Search...",
              })}
              aria-label={i18n._({
                id: "common.multiselect.searchPlaceholder",
                message: "Search...",
              })}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearchChange?.(e.target.value);
              }}
              className="pl-8"
            />
          </div>
        </div>

        <div
          ref={scrollRef}
          className="overflow-y-auto overflow-x-hidden"
          style={{ height: `min(60vh, ${maxHeight}px)` }}
        >
          <div className="w-full">
            {/* A failed search is announced even when seeded options are still on
                screen - otherwise the user reads a stale list as if it were the
                answer to their query. An in-flight retry hides it: the header
                spinner is the truthful state then. */}
            {isError && !isSearching && errorMessage && (
              <div role="alert" className="px-3 py-3 text-center text-sm text-destructive">
                {errorMessage}
              </div>
            )}

            {/* Suggested options */}
            {filteredSuggested.length > 0 && (
              <div>
                {filteredSuggested.map(renderOptionItem)}
                {filteredRegular.length > 0 && (
                  <div className="px-3 py-2">
                    <Separator />
                  </div>
                )}
              </div>
            )}

            {/* Regular options */}
            {filteredRegular.length > 0 && (
              <div>{filteredRegular.map(renderOptionItem)}</div>
            )}

            {/* Empty state. Precedence: an in-flight search wins over an error
                (the spinner in the search header already says "loading"), and an
                error wins over "no results" - a query that never answered must
                not be presented as a confident "nothing matched". */}
            {filteredSuggested.length === 0 &&
              filteredRegular.length === 0 &&
              !isSearching &&
              !isError && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {i18n._({
                    id: "common.multiselect.noResults",
                    message: "No results found",
                  })}
                </div>
              )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

EnhancedSingleSelect.displayName = "EnhancedSingleSelect";

export { EnhancedSingleSelect };
